/**
 * HTTP audit service — gateway `/audit` endpoints.
 */

import { apiClient, toServiceError } from '@/services/http/client'
import { toCamelDeep } from '@/services/http/caseMap'
import type { AuditService, AuditStatsResult } from './audit.service'
import type { AuditLogEntry } from '@/types/session.types'

function mapEntryList(raw: unknown): AuditLogEntry[] {
  const arr = Array.isArray(raw) ? raw : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(r => ({
    ...toCamelDeep<AuditLogEntry>(r),
    details: r.details as AuditLogEntry['details'],
  }))
}

const EMPTY_AUDIT_STATS: AuditStatsResult = {
  stats: { totalEntries: 0, successRate: 0, byAction: [], recentFailures: 0 },
  loginStats: { totalLogins: 0, failedLogins: 0, uniqueUsers: 0, topLocations: [] },
}

export function createHttpAuditService(): AuditService {
  return {
    async listAll(): Promise<AuditLogEntry[]> {
      try {
        const res = await apiClient.get('/audit')
        return mapEntryList(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listForUser(userId: string): Promise<AuditLogEntry[]> {
      try {
        const res = await apiClient.get('/audit', { params: { user_id: userId } })
        return mapEntryList(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listForResource(resourceType: string, resourceId: string): Promise<AuditLogEntry[]> {
      // The backend exposes a dedicated path for resource-scoped lookups:
      //   GET /audit/resources/{resource_type}/{resource_id}
      // (the `/audit` list endpoint filters by resource_type only, not by id).
      try {
        const res = await apiClient.get(`/audit/resources/${resourceType}/${resourceId}`, {
          params: { page: 1, page_size: 100 },
        })
        return mapEntryList(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listRecentLogins(limit: number): Promise<AuditLogEntry[]> {
      try {
        const res = await apiClient.get('/audit', {
          params: { action: 'login', limit },
        })
        return mapEntryList(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getStats(): Promise<AuditStatsResult> {
      // Non-critical aggregate; degrade gracefully if the endpoint is absent.
      try {
        const res = await apiClient.get('/audit/stats')
        return toCamelDeep<AuditStatsResult>(res.data)
      } catch {
        return EMPTY_AUDIT_STATS
      }
    },
  }
}
